import {
	createAsyncThunk,
	createSlice,
	isAnyOf,
	nanoid,
} from '@reduxjs/toolkit'
import { orderBy } from 'lodash'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { clientApi } from 'src/redux/api/graphql/clientApi'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type { GetClientsQueryApi, ReduxClients } from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { fetchClientsUtil } from 'src/utils/fetchUtils'
import { makeErrorToast, msg } from 'src/utils/makeToast'

type ClientState = {
	clientsData: ReduxClients[]
	fetchClientsApiStatus: ApiStatus
	createOrUpdateClientApiStatus: ApiStatus
	deleteClientApiStatus: ApiStatus
	clientModalOpenStatus: boolean
	clientDeleteDialogOpenStatus: boolean
	currentClient: ReduxClients | null
	error?: string | null
}
const initialState: ClientState = {
	clientsData: [],
	fetchClientsApiStatus: 'idle',
	createOrUpdateClientApiStatus: 'idle',
	deleteClientApiStatus: 'idle',
	clientModalOpenStatus: false,
	clientDeleteDialogOpenStatus: false,
	currentClient: null,
	error: null,
}

// Fetches Clients that has users tenantId and the active flag is true
export const fetchClients = createAsyncThunk<
	ReduxClients[],
	undefined,
	{
		state: RootState
		rejectValue: string
	}
>('clients/fetchClients', async (arg, { getState, rejectWithValue }) => {
	try {
		const { auth, settings } = getState()
		const sort = settings.sort
		const tenantId = auth.userTableInfo?.currentCompanyTenantId
		if (!tenantId) {
			return rejectWithValue('Tenant ID is not set')
		}
		const items = (await fetchClientsUtil(tenantId)) as ReduxClients[]

		// For any data fetched by datastore we need to stringify & then parse it for it to be serializable and used by redux
		// const parsedClientsData = JSON.parse(JSON.stringify(clientsData))
		let sortedClients = [] as ReduxClients[]
		if (sort === 'date') {
			sortedClients = orderBy(items, ['createdOn'], ['asc'])
		} else if (sort === 'dateM') {
			sortedClients = orderBy(items, ['createdOn'], ['desc'])
		} else if (sort === 'name') {
			sortedClients = orderBy(
				items,
				[(client) => client?.name?.toLowerCase()],
				['asc']
			)
		}
		return sortedClients
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

// Creates a new client item with name provided by user and then fetches clients again
export const createClient = createAsyncThunk<
	void,
	{ clientName: string },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'clients/createClient',
	async ({ clientName }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			// Creates a new item in clients table save the id, name and tenantId that is the same as the user since only companyAdmin can create this
			const newClient = {
				id: nanoid(),
				name: clientName,
				tenantId: tenantId,
				activeFlag: true,
				offlineIds: ['dummyId'],
			} as ReduxClients
			newClient.parentTeamId = newClient.id

			await API.graphql(
				graphqlOperation(mutations.createClients, { input: newClient })
			)
			dispatch(
				clientApi.util.invalidateTags([{ type: 'Client', id: 'LIST' }])
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Updates the name of the clients & fetches the clients again
export const updateClient = createAsyncThunk<
	void,
	{ client: ReduxClients; clientName: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'clients/updateClient',
	async ({ client, clientName }, { dispatch, rejectWithValue }) => {
		// Gets the original client from datastore then updates it
		try {
			const {
				data: { getClients },
			} = (await API.graphql(
				graphqlOperation(queries.getClients, { id: client.id })
			)) as {
				data: GetClientsQueryApi
			}
			const updatedClient = {
				...getClients,
				name: clientName,
			} as Partial<ReduxClients>
			delete updatedClient?._deleted
			delete updatedClient?._lastChangedAt
			delete updatedClient?.projects
			await API.graphql(
				graphqlOperation(mutations.updateClients, {
					input: updatedClient,
				})
			)
			dispatch(
				clientApi.util.invalidateTags([{ type: 'Client', id: 'LIST' }])
			)
		} catch (err: any) {
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Deletes the clients from id & fetches the clients again
export const deleteClient = createAsyncThunk<
	void,
	{ client: ReduxClients },
	{
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>('clients/deleteClient', async ({ client }, { dispatch, rejectWithValue }) => {
	// Gets the original client from datastore then deletes it along with all it's children projects & grids since it's a one to many relationship
	try {
		await API.graphql(
			graphqlOperation(mutations.deleteClientById, {
				clientId: client.id,
			})
		)
		dispatch(
			clientApi.util.invalidateTags([{ type: 'Client', id: 'LIST' }])
		)
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const clientsSlice = createSlice({
	name: 'clients',
	initialState,
	reducers: {
		setClientsModalOpenStatus: (state, action) => {
			state.clientModalOpenStatus = action.payload.status
			state.currentClient = action.payload.client
		},
		setClientsDeleteDialogOpenStatus: (state, action) => {
			state.clientDeleteDialogOpenStatus = action.payload.status
			state.currentClient = action.payload.client
		},
		setClientFetchApiStatus: (state) => {
			state.fetchClientsApiStatus = 'loading'
		},
		sortClients: (state, action) => {
			if (action.payload === 'date') {
				state.clientsData = orderBy(
					state.clientsData,
					['createdOn'],
					['asc']
				)
			} else if (action.payload === 'dateM') {
				state.clientsData = orderBy(
					state.clientsData,
					['createdOn'],
					['desc']
				)
			} else if (action.payload === 'name') {
				state.clientsData = orderBy(
					state.clientsData,
					[(client) => client?.name?.toLowerCase()],
					['asc']
				)
			}
		},
		clearClientsData: (state) => {
			state.clientsData = []
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchClients.pending, (state, action) => {
			state.fetchClientsApiStatus = 'loading'
		})
		builder.addCase(fetchClients.fulfilled, (state, action) => {
			state.fetchClientsApiStatus = 'succeeded'
			state.clientsData = action.payload
		})
		builder.addCase(fetchClients.rejected, (state, action) => {
			state.fetchClientsApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(createClient.pending, (state, action) => {
			state.createOrUpdateClientApiStatus = 'loading'
		})
		builder.addCase(createClient.fulfilled, (state, action) => {
			state.createOrUpdateClientApiStatus = 'succeeded'
			state.clientModalOpenStatus = false
		})
		builder.addCase(createClient.rejected, (state, action) => {
			state.createOrUpdateClientApiStatus = 'failed'
			state.clientModalOpenStatus = false
			state.error = action.error.message
		})
		builder.addCase(updateClient.pending, (state, action) => {
			state.createOrUpdateClientApiStatus = 'loading'
		})
		builder.addCase(updateClient.fulfilled, (state, action) => {
			state.createOrUpdateClientApiStatus = 'succeeded'
			state.clientModalOpenStatus = false
		})
		builder.addCase(updateClient.rejected, (state, action) => {
			state.createOrUpdateClientApiStatus = 'failed'
			state.clientModalOpenStatus = false
			state.error = action.error.message
		})
		builder.addCase(deleteClient.pending, (state, action) => {
			state.deleteClientApiStatus = 'loading'
		})
		builder.addCase(deleteClient.fulfilled, (state, action) => {
			state.deleteClientApiStatus = 'succeeded'
			state.clientDeleteDialogOpenStatus = false
		})
		builder.addCase(deleteClient.rejected, (state, action) => {
			state.deleteClientApiStatus = 'failed'
			state.clientDeleteDialogOpenStatus = false
			state.error = action.error.message
		})
		builder.addMatcher(
			isAnyOf(
				clientApi.endpoints.updateClientById.matchFulfilled,
				clientApi.endpoints.createClientById.matchFulfilled
			),
			(state, { payload }) => {
				state.clientModalOpenStatus = false
			}
		)
		builder.addMatcher(
			isAnyOf(
				clientApi.endpoints.deleteClientById.matchFulfilled
				// clientApi.endpoints.deleteClientById.matchRejected
			),
			(state, { payload }) => {
				state.clientDeleteDialogOpenStatus = false
			}
		)
		builder.addMatcher(
			isAnyOf(clientApi.endpoints.deleteClientById.matchRejected),
			(state, { payload, error }) => {
				state.clientDeleteDialogOpenStatus = false
				console.log('error: ', error)
			}
		)
	},
})

export const {
	setClientsModalOpenStatus,
	setClientsDeleteDialogOpenStatus,
	setClientFetchApiStatus,
	sortClients,
	clearClientsData,
} = clientsSlice.actions

export default clientsSlice.reducer
